import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";

const GREETING = {
    role: "assistant",
    text:
        "Hello — I'm Camila, the López Hill concierge. I'd love to help you plan a wedding at the hacienda. What date or season are you imagining?",
};

export default function Chatbot() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, open]);

    const send = async () => {
        const text = input.trim();
        if (!text || sending) return;
        const next = [...messages, { role: "user", text }];
        setMessages(next);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/chat", { session_id: sessionId, message: text });
            setSessionId(data.session_id);
            setMessages([...next, { role: "assistant", text: data.reply }]);
        } catch {
            setMessages([
                ...next,
                {
                    role: "assistant",
                    text: "I'm having trouble reaching the gallery just now. Please leave your email at the booking form and we will follow up personally.",
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen((v) => !v)}
                className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-lg hover:bg-[hsl(var(--primary-hover))] transition-all duration-300 flex items-center justify-center"
                data-testid="chatbot-toggle"
                aria-label="Open chat"
            >
                <AnimatePresence mode="wait" initial={false}>
                    {open ? (
                        <motion.span
                            key="x"
                            initial={{ rotate: -45, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 45, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <X className="h-6 w-6" />
                        </motion.span>
                    ) : (
                        <motion.span
                            key="msg"
                            initial={{ rotate: 45, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -45, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <MessageCircle className="h-6 w-6" strokeWidth={1.5} />
                        </motion.span>
                    )}
                </AnimatePresence>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.96 }}
                        transition={{ duration: 0.3 }}
                        className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[400px] h-[540px] bg-background border border-border shadow-2xl flex flex-col"
                        data-testid="chatbot-panel"
                    >
                        <header className="p-5 border-b border-border bg-[hsl(var(--muted))]">
                            <div className="font-serif text-xl tracking-tight">Camila</div>
                            <div className="overline text-[10px] mt-1">López Hill concierge</div>
                        </header>

                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                    data-testid={`chat-msg-${m.role}-${i}`}
                                >
                                    <div
                                        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                                            m.role === "user"
                                                ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                                                : "bg-[hsl(var(--muted))] text-foreground"
                                        }`}
                                    >
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            {sending && (
                                <div className="text-xs text-muted-foreground italic" data-testid="chat-typing">
                                    Camila is writing…
                                </div>
                            )}
                        </div>

                        <div className="p-3 border-t border-border flex items-end gap-2">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKey}
                                placeholder="Ask about dates, capacity, the courtyard…"
                                rows={1}
                                className="flex-1 resize-none px-3 py-2 text-sm bg-transparent border border-border focus:outline-none focus:border-[hsl(var(--primary))]"
                                data-testid="chat-input"
                            />
                            <button
                                onClick={send}
                                disabled={sending || !input.trim()}
                                className="h-10 w-10 flex items-center justify-center bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary-hover))] disabled:opacity-50 transition-colors"
                                data-testid="chat-send-btn"
                                aria-label="Send"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
